import { useEffect, useState } from "react";
import { Box, Button, Collapse, Flex, SimpleGrid, Text, useToast } from "@chakra-ui/react";
import { crm } from "../../../theme";
import {
  getRegistrationSettings,
  RegProcessSettings,
  RegSettingsView,
  saveRegistrationSettings,
} from "../../../services/govRegistrationService";
import { AddressFields, DocumentsEditor, Field, ItemSettingsFields, TextInput } from "./RegistrationFields";

const sectionTitle = { fontSize: "13px", fontWeight: "700", color: crm.text, mb: "8px", mt: "16px" };

/** "Dados da DUIMP" (colapsável): valores que antes eram fixos/config. Pré-preenchidos pelos padrões (Configurações › Registro DUIMP). */
const DuimpDataSection = ({ processId, onSaved }: { processId: string; onSaved: () => Promise<unknown> | void }) => {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<RegSettingsView | null>(null);
  const [form, setForm] = useState<RegProcessSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [openItem, setOpenItem] = useState<number | null>(null);

  const apply = (v: RegSettingsView) => {
    setView(v);
    setForm({ ...v.values, documents: v.values.documents ?? [], itemDefaults: v.values.itemDefaults ?? {}, items: v.values.items ?? {} });
  };

  useEffect(() => {
    getRegistrationSettings(processId)
      .then(apply)
      .catch(() => toast({ title: "Falha ao carregar os dados da DUIMP", status: "error" }));
  }, [processId]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      apply(await saveRegistrationSettings(processId, form));
      toast({ title: "Dados da DUIMP salvos", status: "success" });
      await onSaved();
    } catch (e: any) {
      toast({ title: "Não foi possível salvar", description: e.message, status: "error", duration: 10000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const set = (patch: Partial<RegProcessSettings>) => setForm((f) => (f ? { ...f, ...patch } : f));

  return (
    <Box px="20px" py="12px" borderBottom={`1px solid ${crm.border}`}>
      <Flex align="center" justify="space-between">
        <Box>
          <Text fontWeight="700" fontSize="14px" color={crm.text}>
            Dados da DUIMP
          </Text>
          <Text fontSize="12px" color={crm.textMuted}>
            Unidade RFB, local de consumo, documentos de instrução e condições dos itens (pré-preenchidos pelos padrões da empresa).
          </Text>
        </Box>
        <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} isDisabled={!form}>
          {open ? "Recolher" : "Editar"}
        </Button>
      </Flex>
      <Collapse in={open} animateOpacity>
        {form && view && (
          <Box mt="8px">
            <Text {...sectionTitle} mt="4px">
              Processo
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
              <TextInput label="Unidade RFB de despacho (7 dígitos)" value={form.declaredUnitCode} maxLength={7} placeholder="ex.: 0817800" onChange={(v) => set({ declaredUnitCode: v })} />
              <TextInput label="UF do local de consumo" value={form.consumptionUf} maxLength={2} onChange={(v) => set({ consumptionUf: v?.toUpperCase() ?? null })} />
              <TextInput label="Município IBGE (7 dígitos)" value={form.consumptionCityIbge} maxLength={7} onChange={(v) => set({ consumptionCityIbge: v })} />
            </SimpleGrid>

            <Text {...sectionTitle}>Documentos de instrução</Text>
            <DocumentsEditor
              value={form.documents ?? []}
              onChange={(d) => set({ documents: d })}
              hint="Valores aceitam {invoice}, {bl} e {ce}. Documento sem palavra-chave preenchida fica fora do envio."
            />

            <Text {...sectionTitle}>Condições dos itens (padrão para todos)</Text>
            <ItemSettingsFields value={form.itemDefaults} domains={view.domains} onChange={(v) => set({ itemDefaults: v })} />

            <Text {...sectionTitle}>Exceções por item</Text>
            {view.items.map((it) => {
              const ov = form.items[String(it.number)] ?? {};
              const customized = Object.values(ov).some((x) => x !== null && x !== undefined && x !== "");
              return (
                <Box key={it.number} mb="8px" border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm}>
                  <Flex align="center" justify="space-between" px="12px" py="8px" cursor="pointer" onClick={() => setOpenItem(openItem === it.number ? null : it.number)}>
                    <Text fontSize="13px" color={crm.text}>
                      <b>Item {it.number}</b> · {it.product ?? "sem descrição"} {customized && <Text as="span" color={crm.brand}>· personalizado</Text>}
                    </Text>
                    <Text fontSize="12px" color={crm.textMuted}>
                      {openItem === it.number ? "Recolher" : "Personalizar"}
                    </Text>
                  </Flex>
                  <Collapse in={openItem === it.number} animateOpacity>
                    <Box px="12px" pb="12px">
                      <ItemSettingsFields
                        value={ov}
                        inherit={form.itemDefaults}
                        domains={view.domains}
                        onChange={(v) => set({ items: { ...form.items, [String(it.number)]: v } })}
                      />
                      {customized && (
                        <Button size="xs" variant="ghost" mt="8px" onClick={() => set({ items: { ...form.items, [String(it.number)]: {} } })}>
                          Limpar exceções do item
                        </Button>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {!view.exporterRegistered && (
              <>
                <Text {...sectionTitle}>Endereço do exportador{view.exporterName ? ` (${view.exporterName})` : ""}</Text>
                <Text fontSize="12px" color={crm.textFaint} mb="8px">
                  Usado ao cadastrar o exportador no Catálogo de Operadores Estrangeiros. Em branco, vai "NAO INFORMADO".
                </Text>
                <AddressFields value={form.exporterAddress} onChange={(a) => set({ exporterAddress: a })} />
              </>
            )}

            <Flex justify="flex-end" mt="16px">
              <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
                Salvar dados da DUIMP
              </Button>
            </Flex>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default DuimpDataSection;
